/**
 * The app theme: Grand (light, default) and Slate (dark).
 *
 * Pure functions plus two thin DOM/storage adapters. No Firebase import —
 * the caller supplies the settings document, so this file is testable.
 *
 * ── The bug this exists to fix ────────────────────────────────────────────
 * Trey, 17 Aug 2026: "the slate theme doesn't carry over." It did not, for
 * two compounding reasons:
 *
 *   1. `/v2` had no theme at all. There was one `:root` and no way to pick
 *      anything, so there was nothing for a preference to carry INTO.
 *   2. The two programs cannot see each other's local storage. `/app` writes
 *      the raw string 'slate' to `u:<uid>:mbm_theme` (MBMStore.prefix,
 *      app.html:4054). `/v2`'s `lib/store.js` writes JSON to
 *      `mbm:<uid>:mbm_theme`. Different prefix AND different encoding, so
 *      even a correct reader on the /v2 side would have found nothing.
 *
 * The fix is to treat the SHARED Firestore settings document as the source of
 * truth — `users/{uid}/settings/main`, key `mbm_theme`, `{v, at}` — which is
 * where `/app` already syncs it (app.html:4149, SYNCED_SETTINGS). Local
 * storage on either side is a cache in front of that.
 *
 * When this writes, it writes BOTH the cloud document and `/app`'s own
 * `u:<uid>:mbm_theme` key, in `/app`'s raw-string format. Switching theme in
 * `/v2` therefore changes `/app` too, immediately, without waiting for a sync
 * round trip. That is the behaviour "carry over" actually means.
 */

/** The two themes that ever shipped. `Theme_Classic_*` and
 *  `Theme_Executive_*` at the project root are unreferenced mockups. */
export const THEMES = Object.freeze(['grand', 'slate'])

/** Legacy default, app.html:4311 / 7555. */
export const DEFAULT_THEME = 'grand'

/** The settings key, shared with `/app`. Do not rename. */
export const THEME_KEY = 'mbm_theme'

/** The body class legacy uses. Kept identical so ported CSS keeps working. */
export const SLATE_CLASS = 'theme-slate'

/**
 * An un-namespaced copy of the theme, read by `/app`'s boot overlay before
 * Firebase has resolved who is signed in. Written by both programs.
 * Not a source of truth — see `writeLegacyTheme`.
 */
export const LAST_THEME_HINT = 'mbm_last_theme'

/**
 * Coerce anything into a theme this app can render.
 *
 * Falls back rather than throwing: a corrupt settings value must not take the
 * whole screen down, and Grand is the safe direction — a light theme on a
 * user who wanted dark is a disappointment, an unstyled page is a fault.
 *
 * @param {unknown} value
 * @returns {'grand'|'slate'}
 */
export function normalizeTheme(value) {
  const name = String(value == null ? '' : value)
    .trim()
    .toLowerCase()
  return THEMES.includes(name) ? /** @type {'grand'|'slate'} */ (name) : DEFAULT_THEME
}

/** The other one. */
export function otherTheme(theme) {
  return normalizeTheme(theme) === 'slate' ? 'grand' : 'slate'
}

/** Human label, matching the legacy settings modal (app.html:3434, 3439). */
export function themeLabel(theme) {
  return normalizeTheme(theme) === 'slate' ? 'Slate' : 'Grand'
}

/** The one-line description legacy showed under each option. */
export function themeDescription(theme) {
  return normalizeTheme(theme) === 'slate' ? 'Dark & refined' : 'Light & warm'
}

/**
 * `/app`'s localStorage key for a uid. Its own format, not `/v2`'s.
 *
 * MBMStore namespaces every key as `'u:' + (uid || 'guest') + ':' + key`
 * (app.html:4054) and stores the BARE string, not JSON. Reproduced exactly —
 * a `/v2` write in the wrong shape would be read by `/app` as a theme named
 * `"slate"` *with the quotes*, which `normalizeTheme` would then reject and
 * silently reset to Grand on the next load.
 *
 * @param {string} uid
 * @returns {string}
 */
export function legacyThemeKey(uid) {
  return `u:${uid || 'guest'}:${THEME_KEY}`
}

/**
 * Decide the theme to show, from every source, in priority order.
 *
 * @param {{settings?: any, legacyLocal?: unknown}} sources
 * @returns {'grand'|'slate'}
 */
export function resolveTheme(sources = {}) {
  const wrapped = sources.settings && sources.settings[THEME_KEY]
  /* The settings document stores `{v, at}`; `v` is always a string. A bare
     value is accepted too, because a hand-edited document should still work
     rather than mysteriously reverting. */
  const fromCloud = wrapped && typeof wrapped === 'object' ? wrapped.v : wrapped
  if (fromCloud != null && String(fromCloud).trim() !== '') return normalizeTheme(fromCloud)
  if (sources.legacyLocal != null && String(sources.legacyLocal).trim() !== '') {
    return normalizeTheme(sources.legacyLocal)
  }
  return DEFAULT_THEME
}

/**
 * Put the theme on the page. The only DOM this module touches.
 *
 * Idempotent, and safe to call before the body exists (the boot script calls
 * it against documentElement first — see `bootTheme`).
 *
 * @param {unknown} theme
 * @param {Document} [doc]
 */
export function applyTheme(theme, doc = document) {
  const name = normalizeTheme(theme)
  const targets = [doc.body, doc.documentElement].filter(Boolean)
  for (const el of targets) {
    el.classList.toggle(SLATE_CLASS, name === 'slate')
  }
  return name
}

/**
 * Which theme is on the page right now.
 *
 * The class on the page IS the current theme — `applyTheme` is the only thing
 * that sets it, and both storage layers feed through it. Reading the DOM is
 * therefore not a shortcut around state; it is reading the state.
 *
 * Added 27 Aug 2026 when the Concierge's own settings panel gained a theme
 * row. `features/settings/index.js` had been doing this inline with its own
 * `classList.contains('theme-slate')`; a SECOND feature copying that line is
 * how two controls for one setting start disagreeing about what is on. One
 * reader, one class name, one place to change it.
 *
 * @param {Document} [doc]
 * @returns {'grand'|'slate'}
 */
export function currentTheme(doc = document) {
  const el = (doc && doc.body) || (doc && doc.documentElement)
  return el && el.classList.contains(SLATE_CLASS) ? 'slate' : DEFAULT_THEME
}

/**
 * Read `/app`'s copy for this user, if the browser has one.
 * @param {string} uid
 * @returns {string|null}
 */
export function readLegacyTheme(uid) {
  try {
    return localStorage.getItem(legacyThemeKey(uid))
  } catch {
    /* Private mode, or storage disabled. Not knowing is not an error here. */
    return null
  }
}

/**
 * Write `/app`'s copy, so switching in `/v2` changes `/app` immediately.
 * @param {string} uid
 * @param {unknown} theme
 */
export function writeLegacyTheme(uid, theme) {
  const name = normalizeTheme(theme)
  try {
    localStorage.setItem(legacyThemeKey(uid), name)
    /* Also the un-namespaced hint `/app`'s boot overlay reads. That overlay
       runs before Firebase has said who is signed in, so it cannot use the
       namespaced key — and without this it painted #FFFFFF full-screen at a
       Slate advisor every time a trip was opened. `/app` writes the same key
       from applyAppearanceClasses(). Not a source of truth: a stale value
       costs one wrong-coloured splash, never a wrong-coloured app. */
    localStorage.setItem(LAST_THEME_HINT, name)
    return true
  } catch {
    return false
  }
}
